import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    getAccountFromRule,
    type IncomeSplitConfigLegacy,
    IncomeSplitRuleCard,
    type IncomeSplitRuleLegacy,
} from '@fruitful/income-split';
import type { DepositAccount } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { sortBy } from 'lodash';
import React from 'react';
import { View } from 'smoothie';

import {
    incomeSplitAtomLegacy,
    incomeSplitRuleEditorModeAtom,
    selectedIncomeRuleAtomLegacy,
} from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';

const SortableItem = ({
    rule,
    children,
}: {
    rule: IncomeSplitRuleLegacy;
    children: (dragHandleProps: Record<string, unknown>) => React.ReactNode;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: rule.id });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        marginBottom: 16,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {children({ ...listeners })}
        </div>
    );
};

const IncomeSplitRules = ({
    incomeSplit,
    externalAccounts,
    setBoxHeights,
    setIsSorting,
}: {
    incomeSplit: IncomeSplitConfigLegacy;
    externalAccounts: DepositAccount[];
    setBoxHeights: (
        arg0: (prev: Record<string, number>) => Record<string, number>,
    ) => void;
    setIsSorting: (arg0: boolean) => void;
}) => {
    const setIncomeSplit = useSetAtom(incomeSplitAtomLegacy);
    const setSelectedRule = useSetAtom(selectedIncomeRuleAtomLegacy);
    const setIncomeSplitRuleEditorMode = useSetAtom(
        incomeSplitRuleEditorModeAtom,
    );
    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);

    const rules = sortBy(incomeSplit.rules ?? [], (rule) => rule.order);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setIsSorting(false);

        if (!over || active.id === over.id) return;

        const oldIndex = rules.findIndex((r) => r.id === active.id);
        const newIndex = rules.findIndex((r) => r.id === over.id);

        const reordered = arrayMove(rules, oldIndex, newIndex).map((r, i) => ({
            ...r,
            order: i,
        }));

        setIncomeSplit({ ...incomeSplit, rules: reordered });
    };

    const Rule = ({
        rule,
        dragHandleProps,
    }: {
        rule: IncomeSplitRuleLegacy;
        dragHandleProps: Record<string, unknown>;
    }) => {
        const account = getAccountFromRule(
            rule,
            bankAccountDetails,
            externalAccounts,
        );
        if (!account) return null;

        const { color, icon } = account;

        return (
            <View
                onLayout={(e) => {
                    const height = e.nativeEvent?.layout?.height;
                    if (height === undefined) return;

                    const storedHeight = height + 16;
                    setBoxHeights((prev) => {
                        if (prev[rule.id] === storedHeight) return prev;
                        return {
                            ...prev,
                            [rule.id]: storedHeight,
                        };
                    });
                }}
            >
                <IncomeSplitRuleCard
                    rule={rule}
                    account={account}
                    color={color}
                    icon={icon}
                    context="edit"
                    dragHandleProps={dragHandleProps}
                    onPressEdit={() => {
                        setSelectedRule(rule);
                        setIncomeSplitRuleEditorMode('Edit');
                        router.navigate('/income-split/edit');
                    }}
                    onPressClose={() => {
                        setIncomeSplit({
                            ...incomeSplit,
                            rules: (incomeSplit?.rules ?? []).filter(
                                (r) => rule.id !== r.id,
                            ),
                        });
                    }}
                />
            </View>
        );
    };

    return (
        <View>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={() => {
                    setIsSorting(true);
                }}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToParentElement]}
            >
                <SortableContext
                    items={rules.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {rules.map((rule) => (
                        // eslint-disable-next-line react/prop-types
                        <SortableItem key={rule.id} rule={rule}>
                            {(dragHandleProps) => (
                                <Rule
                                    rule={rule}
                                    dragHandleProps={dragHandleProps}
                                />
                            )}
                        </SortableItem>
                    ))}
                </SortableContext>
            </DndContext>
        </View>
    );
};

export default IncomeSplitRules;
